import { useState, useEffect, useCallback } from 'react';
import i18n from 'i18next';
import {
  Button,
  Alert,
  Stack,
  LinearProgress,
  Select,
  Option,
  Typography,
  Input,
  Snackbar,
  Grid,
  FormLabel,
  FormControl,
  Textarea
} from '@mui/joy';
import {
  getAuth,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { getMessaging, getToken } from 'firebase/messaging';
import { doc, getFirestore, updateDoc, arrayUnion } from 'firebase/firestore';
import { Check, Warning, Info } from '@mui/icons-material';
import { useUser } from '../context/user';
import { useTranslation } from 'react-i18next';

// Firebase Auth normalises email addresses to lower case, so a participant
// retyping their address with different capitalisation is not a real change.
// Compare case-insensitively, otherwise the (case-sensitive) comparison below
// sends a change request for an address the account already owns, which
// Firebase rejects with auth/email-already-exists.
const normaliseEmail = email => (email || '').trim().toLowerCase();

const Settings = () => {
  const { t } = useTranslation();
  const [notificationStatus, setNotificationStatus] = useState('prompt');
  const [isLoading, setIsLoading] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const { user, userAuth } = useUser();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: ''
  });
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    color: 'success'
  });
  const [deviceInfo, setDeviceInfo] = useState({
    isIOS: false,
    isPWA: false,
    supportsNotifications: true,
    version: null
  });

  const languages = [
    {
      value: 'english',
      label: 'English'
    },
    {
      value: 'hebrew',
      label: 'עברית'
    }
    // Add more languages as needed
  ];

  // Detect iOS version and notification support
  const detectiOSAndNotificationSupport = useCallback(() => {
    // Check if device is iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (!isIOS)
      return { isIOS: false, isPWA: false, supportsNotifications: true };

    // Check if already in PWA mode
    const isPWA = window.navigator.standalone;

    // Extract iOS version from user agent
    const matches = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    let version = null;

    if (matches) {
      version = {
        major: parseInt(matches[1], 10),
        minor: parseInt(matches[2], 10),
        patch: parseInt(matches[3] || 0, 10)
      };
    }

    // iOS 16.4+ supports notifications (in PWA only)
    const supportsNotifications =
      version &&
      (version.major > 16 || (version.major === 16 && version.minor >= 4));

    return { isIOS, isPWA, version, supportsNotifications };
  }, []);

  const updateToken = useCallback(async () => {
    try {
      const messaging = getMessaging();
      let token;
      try {
        token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_VAPIDKEY
        });
      } catch (error) {
        if (error.code === 'messaging/token-unsubscribe-failed') {
          console.warn(
            'Failed to unsubscribe from FCM, proceeding to get new token'
          );
          token = await getToken(
            messaging,
            {
              vapidKey: import.meta.env.VITE_VAPIDKEY
            },
            true
          );
        } else {
          throw error;
        }
      }

      if (token) {
        await updateDoc(doc(getFirestore(), 'participants', user.id), {
          fcmTokens: arrayUnion(token)
        });
      } else {
        console.warn('No FCM token received');
      }
    } catch (error) {
      console.error('Error getting token or updating Firestore:', error);
    }
  }, [user]);

  const requestPermission = async () => {
    setIsLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setNotificationStatus(permission);
      if (permission === 'granted') {
        await updateToken();
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      setNotificationStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Detect iOS version and notification support
    const info = detectiOSAndNotificationSupport();
    setDeviceInfo(info);

    const checkAndUpdateNotificationStatus = async () => {
      if ('permissions' in navigator && 'Notification' in window) {
        const permission = await navigator.permissions.query({
          name: 'notifications'
        });

        const updateStatus = () => {
          const currentPermission = Notification.permission;
          setNotificationStatus(currentPermission);
          if (currentPermission === 'granted' && user) {
            updateToken();
          }
        };

        updateStatus();
        permission.onchange = updateStatus;

        return () => {
          permission.onchange = null;
        };
      } else {
        setNotificationStatus('unsupported');
      }
    };

    checkAndUpdateNotificationStatus();
  }, [user, updateToken, detectiOSAndNotificationSupport]);

  const handleLanguageChange = async newValue => {
    if (newValue) {
      try {
        await updateDoc(doc(getFirestore(), 'participants', user.id), {
          language: newValue
        });
        i18n.changeLanguage(newValue);
      } catch (error) {
        console.error('Error updating language in Firestore:', error);
      }
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        email: userAuth.email || '',
        personalNotes: user.personalNotes || ''
      });
    }
  }, [user, userAuth]);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const reauthenticateUser = async () => {
    const password = prompt(t('settings.passwordPrompt'));
    if (password) {
      const credential = EmailAuthProvider.credential(userAuth.email, password);
      await reauthenticateWithCredential(userAuth, credential);
    } else {
      throw new Error('Password not provided');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setProcessing(true);
    try {
      const auth = getAuth();
      const db = getFirestore();

      // Update email if changed
      if (normaliseEmail(formData.email) !== normaliseEmail(userAuth.email)) {
        try {
          await reauthenticateUser();
          await verifyBeforeUpdateEmail(auth.currentUser, formData.email);
          setEmailVerificationSent(true);
          setSnackbar({
            open: true,
            message:
              'Verification email sent. Please check your new email and verify before the change takes effect.',
            color: 'primary'
          });
        } catch (error) {
          console.error('Error updating email:', error);
          setSnackbar({
            open: true,
            message: 'Error updating email. Please try again.',
            color: 'danger'
          });
          setProcessing(false);
          return;
        }
      }

      // Update Firestore document (including email so the doc stays in sync)
      await updateDoc(doc(db, 'participants', userAuth.uid), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        personalNotes: formData.personalNotes
      });

      setSnackbar({
        open: true,
        message: 'Profile updated successfully',
        color: 'success'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: 'Error updating profile',
        color: 'danger'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (!user) {
    return <LinearProgress />;
  }

  const renderNotificationSection = () => {
    const { isIOS, isPWA, supportsNotifications } = deviceInfo;

    // Special case: iOS device in browser mode
    if (isIOS && !isPWA) {
      if (supportsNotifications) {
        // iOS 16.4+ but in browser (needs PWA installation)
        return (
          <Alert
            color="warning"
            variant="soft"
            startDecorator={<Info />}
            sx={{ maxWidth: '100%' }}
          >
            <Stack spacing={1}>
              <Typography level="body-md">
                You're currently using ELFS in a web browser. On iOS,
                notifications are only available when using the app installed on
                your home screen.
              </Typography>
              <Typography level="body-sm">
                To install, tap the share icon{' '}
                <span style={{ fontSize: '1.1em' }}>⎙</span> and select "Add to
                Home Screen"
              </Typography>
            </Stack>
          </Alert>
        );
      } else {
        // Older iOS that doesn't support notifications at all
        return (
          <Alert color="warning" variant="soft" startDecorator={<Warning />}>
            Your iOS version doesn't support web notifications. Please ensure
            you check your email for survey reminders.
          </Alert>
        );
      }
    }

    // iOS device in PWA mode or non-iOS devices - show normal notification UI
    switch (notificationStatus) {
      case 'unsupported':
        return (
          <Alert color="warning" variant="soft" startDecorator={<Warning />}>
            {t('settings.noNotificationSupport')}
          </Alert>
        );
      case 'denied':
        return (
          <Alert color="warning" variant="soft" startDecorator={<Warning />}>
            {t('settings.notificationDenied')}
          </Alert>
        );
      case 'granted':
        return (
          <Alert color="success" variant="soft" startDecorator={<Check />}>
            {t('settings.notificationsEnabled')}
          </Alert>
        );
      case 'error':
        return (
          <Alert color="error" variant="soft" startDecorator={<Warning />}>
            {t('settings.errorRequestingPermission')}
          </Alert>
        );
      case 'prompt':
      default:
        return (
          <Alert
            color="warning"
            endDecorator={
              <Button
                onClick={requestPermission}
                size="sm"
                variant="solid"
                color="success"
                loading={isLoading}
              >
                {t('settings.enableNotifications')}
              </Button>
            }
          >
            {t('settings.pleaseEnableNotifications')}
          </Alert>
        );
    }
  };

  return (
    <Grid container sx={{ my: 8, mx: 3 }} spacing={3} justifyContent="center">
      <Grid xs={12}>
        <Typography level="h3" textAlign="center" gutterBottom>
          {t('settings.title')}
        </Typography>
        {emailVerificationSent && (
          <Alert color="info">{t('settings.verificationEmailSent')}</Alert>
        )}
      </Grid>
      <Grid xs={12} sm={6} lg={3}>
        <Stack spacing={3}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>{t('settings.firstNameLabel')}</FormLabel>
                <Input
                  name="firstName"
                  placeholder={t('settings.firstNameLabel')}
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('settings.lastNameLabel')}</FormLabel>
                <Input
                  name="lastName"
                  placeholder={t('settings.lastNameLabel')}
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('settings.phoneNumberLabel')}</FormLabel>
                <Input
                  name="phoneNumber"
                  placeholder={t('settings.phoneNumberLabel')}
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('settings.emailLabel')}</FormLabel>
                <Input
                  name="email"
                  placeholder={t('settings.emailLabel')}
                  value={formData.email}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('settings.personalNotesLabel')}</FormLabel>
                <Textarea
                  minRows={3}
                  name="personalNotes"
                  value={formData.personalNotes}
                  onChange={handleChange}
                  placeholder={t('settings.personalNotesPlaceholder')}
                />
              </FormControl>
              <Button
                type="submit"
                loading={processing}
                disabled={
                  formData.firstName === user.firstName &&
                  formData.lastName === user.lastName &&
                  formData.phoneNumber === user.phoneNumber &&
                  normaliseEmail(formData.email) ===
                    normaliseEmail(userAuth.email) &&
                  formData.personalNotes === user.personalNotes
                }
              >
                {t('settings.updateProfileButton')}
              </Button>
            </Stack>
          </form>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            color={snackbar.color}
          >
            {snackbar.message}
          </Snackbar>
        </Stack>
      </Grid>
      <Grid xs={12} sm={6} lg={3}>
        <Stack spacing={3}>
          {renderNotificationSection()}
          <Alert
            color="info"
            endDecorator={
              <Select
                defaultValue={user.language || 'english'}
                onChange={(_, newValue) => handleLanguageChange(newValue)}
              >
                {languages.map(lang => (
                  <Option key={lang.value} value={lang.value}>
                    {lang.label}
                  </Option>
                ))}
              </Select>
            }
          >
            {t('settings.languagePreference')}
          </Alert>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Settings;
